import runtimeConfig from "../../../runtime-config";
import React from "react";
import { useSnackbar } from "notistack";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { resetPayment } from "../../../redux/payment/payment.actions";
import { selectCurrentPayment } from "../../../redux/payment/payment.selectors";
import { resetCustomer } from "../../../redux/customer/customer.actions";
import { selectCurrentCustomer } from "../../../redux/customer/customer.selectors";
import { resetCart } from "../../../redux/cart/cart.actions";
import { selectCartItems, selectCartTotal } from "../../../redux/cart/cart.selectors";
import { resetShipping } from "../../../redux/shipping/shipping.actions";
import { selectCurrentShipping } from "../../../redux/shipping/shipping.selectors";

import { startCheckout, checkoutPayload, matchesBasket, clearAttempt, assertCurrentAttempt } from '../recovery/checkout-attempt';

const API_URI = runtimeConfig.REACT_APP_API_URI;
const options = { "client-id": runtimeConfig.REACT_APP_PAYPAL_CLIENT_ID, currency: "GBP", intent: "capture" };

export const PaypalButton = ({ customer, items, cartTotal, shipping, payment, resetCart, resetCustomer, resetShipping, resetPayment }) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const attempt = React.useRef(null);
  const errorShown = React.useRef(false);
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const total = Number(cartTotal) + Number(shipping.cost);
  const payload = checkoutPayload({ customer, items, cartTotal, shipping, payment }, 'paypal');
  const latestPayload = React.useRef(payload);
  latestPayload.current = payload;
  const fingerprint = JSON.stringify(payload);
  const latest = React.useRef(fingerprint);
  latest.current = fingerprint;

  const showError = (error) => {
    const message = error.response && error.response.data && error.response.data.message;
    enqueueSnackbar(message || error.message || "PayPal could not complete payment. Your basket is retained.", { variant: "error" });
    errorShown.current = true;
  };

  const createOrder = async () => {
    errorShown.current = false;
    try {
      if (!payment.set_billing_address) throw new Error("Please confirm your billing address first.");
      if (attempt.current && attempt.current.fingerprint !== fingerprint) {
        if (confirming) throw new Error("Please finish confirming your previous PayPal payment before starting another.");
        attempt.current = null;
      }
      if (!attempt.current) {
        const saved = await startCheckout(payload);
        attempt.current = { orderId: saved.order.id, orderRef: saved.order.ref, fingerprint, saved: saved.attempt };
      }
      assertCurrentAttempt(attempt.current.saved);
      if (!attempt.current.paypalOrderId) {
        const response = await axios.post(API_URI + "/paypal/create-order", {
          order_id: attempt.current.orderId, order_ref: attempt.current.orderRef, checkout_token: attempt.current.saved.token,
        });
        if (response.data.status !== "success" || !response.data.paypal_order_id) throw new Error("Unable to initialise PayPal.");
        attempt.current.paypalOrderId = response.data.paypal_order_id;
      }
      return attempt.current.paypalOrderId;
    } catch (error) {
      showError(error);
      throw error;
    }
  };

  const confirm = async () => {
    setBusy(true);
    setConfirming(true);
    try {
      const current = attempt.current;
      if (!current || current.fingerprint !== latest.current) throw new Error("Your checkout changed. Return to the original basket details before retrying confirmation.");
      assertCurrentAttempt(current.saved);
      const response = await axios.post(API_URI + "/paypal/" + current.orderId + "/capture", {
        order_ref: current.orderRef, paypal_order_id: current.paypalOrderId, checkout_token: current.saved.token,
      });
      if (response.data.status !== "success" || response.data.payment_status !== "COMPLETED") throw new Error("PayPal confirmation is pending.");
      enqueueSnackbar("Your order has been placed successfully.", { variant: "success" });
      const unchanged = await matchesBasket(current.saved, () => latestPayload.current);
      clearAttempt(current.saved);
      if (unchanged) { resetCart(); resetCustomer(); resetShipping(); resetPayment(); }
      history.push("/order-complete");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p>Order total: <strong>£{total.toFixed(2)}</strong></p>
      {confirming ? (
        <div>
          <p>Finish confirming this PayPal payment before starting another. Your basket is retained until confirmation succeeds.</p>
          <button className="btn btn-blue" disabled={busy} onClick={confirm}>
            {busy ? "Confirming payment…" : "Retry PayPal confirmation"}
          </button>
        </div>
      ) : (
        <PayPalScriptProvider options={options}>
          <PayPalButtons
            style={{ layout: "horizontal" }}
            forceReRender={[fingerprint]}
            createOrder={createOrder}
            onApprove={async (data) => {
              if (!attempt.current || data.orderID !== attempt.current.paypalOrderId) {
                showError(new Error("PayPal returned an unexpected order. Your basket is retained."));
                return;
              }
              await confirm();
            }}
            onCancel={() => enqueueSnackbar("PayPal checkout cancelled. Your basket is retained.", { variant: "info" })}
            onError={() => {
              if (!errorShown.current) showError(new Error("PayPal checkout is unavailable. Your basket is retained."));
            }}
          />
        </PayPalScriptProvider>
      )}
    </div>
  );
};
const mapStateToProps = createStructuredSelector({ customer: selectCurrentCustomer, items: selectCartItems, cartTotal: selectCartTotal, shipping: selectCurrentShipping, payment: selectCurrentPayment });
const mapDispatchToProps = { resetCart, resetCustomer, resetShipping, resetPayment };
export default connect(mapStateToProps, mapDispatchToProps)(PaypalButton);
